import { useMemo, useState } from 'react';

import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Member } from '@/shared/types/member';

import AgentOverrideEditor from './AgentOverrideEditor';
import AgentOverrideRow from './AgentOverrideRow';
import { OverrideDraft } from './types';

type Props = {
  value: OverrideDraft[];
  onChange: (next: OverrideDraft[]) => void;
  members: Member[];
  /** Policy values used as the initial limits when creating a new override. */
  policyDefaults: { max_conversations: number; fair_distribution_limit: number };
  disabled?: boolean;
};

type EditorState = { open: boolean; mode: 'create' | 'edit'; editing?: OverrideDraft };

const AgentOverridesSection = ({ value, onChange, members, policyDefaults, disabled }: Props) => {
  const { t } = useTranslation();
  const [editor, setEditor] = useState<EditorState>({ open: false, mode: 'create' });

  const membersById = useMemo(() => new Map(members.map((m) => [m.user_id, m])), [members]);

  // Agents without an existing override are the only ones selectable when adding.
  const availableMembers = useMemo(() => {
    const taken = new Set(value.map((o) => o.user_id));
    return members.filter((m) => !taken.has(m.user_id));
  }, [members, value]);

  const editingMember = editor.editing ? membersById.get(editor.editing.user_id) : undefined;

  const closeEditor = () => setEditor((s) => ({ ...s, open: false }));

  const handleSave = (drafts: OverrideDraft[]) => {
    // Upsert each draft by user_id (covers both multi-add and single-agent edit).
    const byId = new Map(value.map((o) => [o.user_id, o]));
    drafts.forEach((d) => byId.set(d.user_id, d));
    onChange([...byId.values()]);
    closeEditor();
  };

  const handleDelete = (userId: string) => onChange(value.filter((o) => o.user_id !== userId));

  return (
    <div className="flex flex-col gap-4">
      <Popover open={editor.open} onOpenChange={(open) => setEditor((s) => ({ ...s, open }))}>
        <div className="flex items-center gap-4 rounded-lg bg-bg-muted-50 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm leading-none font-medium text-foreground">
              {t('environments.agent_overrides')}
            </p>
            <p className="mt-1 text-sm leading-5 text-foreground-muted">
              {t('environments.agent_overrides_subtitle')}
            </p>
          </div>
          <PopoverAnchor asChild>
            <Button
              type="button"
              variant="outline-neutral"
              size="sm"
              disabled={disabled || availableMembers.length === 0}
              onClick={() => setEditor({ open: true, mode: 'create', editing: undefined })}
            >
              <PlusIcon className="size-4" />
              {t('common.add')}
            </Button>
          </PopoverAnchor>
        </div>
        <PopoverContent
          align="end"
          className="w-112 p-4"
          onFocusOutside={(e) => e.preventDefault()}
        >
          <AgentOverrideEditor
            key={`${editor.mode}-${editor.editing?.user_id ?? 'new'}`}
            mode={editor.mode}
            availableMembers={availableMembers}
            editingMember={editingMember}
            editing={editor.editing}
            defaults={policyDefaults}
            onSave={handleSave}
            onCancel={closeEditor}
          />
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-col rounded-lg border border-stroke">
          {value.map((override) => (
            <AgentOverrideRow
              key={override.user_id}
              override={override}
              member={membersById.get(override.user_id)}
              disabled={disabled}
              onEdit={() => setEditor({ open: true, mode: 'edit', editing: override })}
              onDelete={() => handleDelete(override.user_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentOverridesSection;
